/**
 * CheckInWidget — multi-session aware with live clock, session timer,
 * and GPS + device + IP capture on every check-in / check-out.
 *
 * BUG FIX (this version) — the 15-20 second delay on every Check In /
 * Check Out click.
 *
 *   ROOT CAUSE: getLocationPayload() was re-fetching GPS from scratch
 *   on EVERY click (getGPS() has an 8-second timeout) AND re-running
 *   reverse geocoding against the free Nominatim API on EVERY click
 *   too — with NO timeout at all on that fetch() call. GPS coordinates
 *   jitter slightly between reads even when standing still, so the
 *   "did position change?" check was essentially always true,
 *   triggering a fresh, unbounded geocoding network call before the
 *   actual (fast) check-in/check-out API request was even sent.
 *   8s (GPS retry) + several more unbounded seconds (Nominatim) =
 *   exactly the 15-20s delay reported.
 *
 *   FIX:
 *     1. Use the pre-fetched `gpsData` from mount directly on click —
 *        location doesn't meaningfully change in the seconds/minutes
 *        between page load and clicking check-in for the vast
 *        majority of real use. Only fetch GPS fresh if it's still
 *        unavailable (e.g. permission was just granted).
 *     2. Added a 4-second timeout to the reverseGeocode() fetch call,
 *        which previously had none — it could hang indefinitely if
 *        Nominatim was slow or rate-limited.
 *     3. Only re-reverse-geocodes if position moved meaningfully
 *        (~100m+), not on every microscopic GPS jitter.
 *     4. Check-in/check-out now fires using whatever location data is
 *        ALREADY available — it never blocks the actual attendance
 *        action waiting on geocoding to finish.
 *
 * BUG FIX (this version) — "needs a page refresh to see updated
 * Check In/Out and Work Hours" on ESS Dashboard.
 *
 *   This widget already calls onStatusChange(data) every time it
 *   fetches status (on mount AND right after check-in/check-out
 *   succeeds) — that mechanism was already correct. The actual bug was
 *   in ESSHub.js, which rendered <CheckInWidget /> without ever
 *   passing the onStatusChange prop, so ESSHub.js's own "Today's
 *   Attendance" card never found out a check-in/out had happened and
 *   kept showing stale data from page-load time. Fixed in ESSHub.js —
 *   see that file's own changelog. No changes needed here for that
 *   part; the prop was already being called, just never listened to.
 *
 * Location capture flow:
 *   1. On mount, pre-fetch GPS position (avoids delay on button click)
 *   2. On Check In / Check Out, send { lat, lng, accuracy, location_name }
 *      in request body alongside the check-in/out call
 *   3. Backend captures IP and User-Agent from the request headers
 *   4. Reverse geocoding uses browser's Nominatim-compatible API (no API key needed)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spinner, Card, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { LogIn, LogOut, Clock, MapPin, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
// ── Time helpers ──────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
const formatCurrentTime = () => {
    const d = new Date();
    const h = d.getHours();
    const m = d.getMinutes();
    const s = d.getSeconds();
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${pad(m)}:${pad(s)} ${ampm}`;
};
const fmtTime = (t) => {
    if (!t) return '—';
    const [h, m] = String(t).split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${pad(m)} ${ampm}`;
};
const timeToSec = (t) => {
    if (!t) return 0;
    const [h, m, s] = String(t).split(':').map(Number);
    return h * 3600 + (m || 0) * 60 + (s || 0);
};
const fmtElapsed = (sec) => {
    if (sec < 0) sec = 0;
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`;
    if (m > 0) return `${pad(m)}m ${pad(s)}s`;
    return `${pad(s)}s`;
};
const fmtWorkHours = (hours) => {
    const h = parseFloat(hours) || 0;
    if (h === 0) return '0 min';
    const totalMins = Math.round(h * 60);
    const hrs  = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hrs === 0) return `${mins} min`;
    if (mins === 0) return `${hrs}h`;
    return `${hrs}h ${mins}m`;
};
const fmtDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, mo, d] = (dateStr || '').split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-IN', {
        weekday: 'short', day: 'numeric', month: 'short',
    });
};
const getLocalToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
// ── Geolocation helpers ───────────────────────────────────────────────────────
const getGPS = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
        resolve(null);
        return;
    }
    navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
            lat:      pos.coords.latitude,
            lng:      pos.coords.longitude,
            accuracy: pos.coords.accuracy,
        }),
        () => resolve(null), // silently fail if denied
        { timeout: 8000, enableHighAccuracy: true, maximumAge: 30000 }
    );
});
// BUG FIX: added a 4-second timeout via AbortController — this fetch()
// previously had NO timeout at all, so a slow/rate-limited Nominatim
// response could hang indefinitely, which was a major contributor to
// the reported 15-20 second check-in/check-out delay.
const reverseGeocode = async (lat, lng) => {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);
        const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
            { headers: { 'Accept-Language': 'en' }, signal: controller.signal }
        );
        clearTimeout(timeoutId);
        const data = await res.json();
        // Build a readable short address
        const a = data.address || {};
        const parts = [
            a.suburb || a.neighbourhood || a.village || a.town,
            a.city   || a.county,
            a.state,
        ].filter(Boolean);
        return parts.slice(0, 2).join(', ') || data.display_name?.split(',').slice(0,2).join(',') || 'Unknown location';
    } catch {
        return null; // timed out, aborted, or network error — proceed without a name
    }
};
// BUG FIX: rough distance check (in meters) using the haversine formula,
// used to decide whether a position has moved ENOUGH to warrant a fresh
// reverse-geocode call. GPS readings jitter by a few meters even when
// standing perfectly still — without this check, the previous code
// re-geocoded on essentially every single click.
const distanceMeters = (lat1, lng1, lat2, lng2) => {
    if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return Infinity;
    const R = 6371000; // Earth radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};
// ════════════════════════════════════════════════════════════════════════════
const CheckInWidget = ({ compact = false, onStatusChange }) => {
    const [status,      setStatus]      = useState(null);
    const [loading,     setLoading]     = useState(false);
    const [currentTime, setCurrentTime] = useState(formatCurrentTime());
    const [elapsed,     setElapsed]     = useState(0);
    const [gpsData,     setGpsData]     = useState(null);  // pre-fetched position
    const [gpsStatus,   setGpsStatus]   = useState('idle'); // idle|fetching|ok|denied
    const timerRef  = useRef(null);
    const clockRef  = useRef(null);
    const onStatusChangeRef = useRef(onStatusChange);
    useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);
    // ── Live clock ────────────────────────────────────────────────────────
    useEffect(() => {
        clockRef.current = setInterval(() => setCurrentTime(formatCurrentTime()), 1000);
        return () => clearInterval(clockRef.current);
    }, []);
    // ── Pre-fetch GPS on mount ────────────────────────────────────────────
    useEffect(() => {
        if (!navigator.geolocation) { setGpsStatus('denied'); return; }
        setGpsStatus('fetching');
        getGPS().then(async (pos) => {
            if (!pos) { setGpsStatus('denied'); return; }
            const locationName = await reverseGeocode(pos.lat, pos.lng);
            setGpsData({ ...pos, location_name: locationName });
            setGpsStatus('ok');
        });
    }, []);
    // ── Session elapsed timer ─────────────────────────────────────────────
    const startTimer = useCallback((checkInTimeStr) => {
        clearInterval(timerRef.current);
        if (!checkInTimeStr) return;
        const tick = () => {
            const now = new Date();
            const nowSec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
            const inSec  = timeToSec(checkInTimeStr);
            const diff   = nowSec - inSec;
            setElapsed(diff > 0 ? diff : 0);
        };
        tick();
        timerRef.current = setInterval(tick, 1000);
    }, []);
    const stopTimer = useCallback(() => {
        clearInterval(timerRef.current);
        setElapsed(0);
    }, []);
    // ── Fetch today status ────────────────────────────────────────────────
    const fetchStatus = useCallback(async () => {
        try {
            const res  = await axiosInstance.get('/attendance/today');
            const data = res.data.data || {};
            setStatus(data);
            if (onStatusChangeRef.current) onStatusChangeRef.current(data);
            if (data.has_open_session && data.check_in) {
                const sessions    = data.sessions || [];
                const openSession = sessions.find(s => s.check_in && !s.check_out);
                startTimer(openSession?.check_in || data.check_in);
            } else if (data.checked_in && !data.checked_out && data.check_in) {
                startTimer(data.check_in);
            } else {
                stopTimer();
            }
        } catch { setStatus({}); }
    }, [startTimer, stopTimer]);
    useEffect(() => {
        fetchStatus();
        return () => clearInterval(timerRef.current);
    }, []); // eslint-disable-line
    // ── Get location payload for the action — FAST, non-blocking ──────────
    // BUG FIX: previously re-fetched GPS from scratch (up to 8s) and
    // re-ran reverse geocoding (previously unbounded) on EVERY click,
    // even though good location data was usually already sitting in
    // `gpsData` from the mount-time pre-fetch. Now:
    //   - Uses the existing pre-fetched gpsData directly when available
    //     — no fresh GPS read, no delay.
    //   - Only attempts a fresh GPS read if gpsData is still missing
    //     (e.g. permission was granted after mount).
    //   - Only re-reverse-geocodes if the position has moved more than
    //     ~100m from the last geocoded point, not on every tiny jitter.
    //   - Never blocks for more than the new 4s geocode timeout, and
    //     never re-fetches GPS itself on click at all anymore.
    const lastGeocodedPos = useRef(null); // { lat, lng } of the last point we actually geocoded
    const getLocationPayload = async () => {
        let pos = gpsData;
        // Only attempt a fresh GPS read if we have NOTHING yet — avoids
        // the 8-second getGPS() timeout firing on every single click.
        if (!pos) {
            pos = await getGPS();
            if (!pos) return {}; // still unavailable — proceed with no location, don't block check-in
        }
        const movedFar = distanceMeters(
            lastGeocodedPos.current?.lat, lastGeocodedPos.current?.lng,
            pos.lat, pos.lng
        ) > 100; // only re-geocode if moved more than ~100 meters
        let location_name = pos.location_name || gpsData?.location_name || null;
        if (movedFar || !location_name) {
            location_name = await reverseGeocode(pos.lat, pos.lng); // now has a 4s timeout
            lastGeocodedPos.current = { lat: pos.lat, lng: pos.lng };
            setGpsData(prev => ({ ...(prev || pos), location_name }));
        }
        return {
            lat:           pos.lat,
            lng:           pos.lng,
            accuracy:      pos.accuracy,
            location_name,
        };
    };
    // ── Check In ──────────────────────────────────────────────────────────
    const handleCheckIn = async () => {
        setLoading(true);
        try {
            const locPayload = await getLocationPayload();
            await axiosInstance.post('/attendance/check-in', locPayload);
            toast.success('Checked in successfully! ✓');
            await fetchStatus();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Check-in failed');
        } finally { setLoading(false); }
    };
    // ── Check Out ─────────────────────────────────────────────────────────
    const handleCheckOut = async () => {
        setLoading(true);
        try {
            const locPayload = await getLocationPayload();
            const res = await axiosInstance.post('/attendance/check-out', locPayload);
            const totalHours = res.data.data?.total_work_hours || res.data.data?.work_hours;
            toast.success(`Checked out. ${totalHours ? fmtWorkHours(totalHours) + ' worked today.' : 'Have a great day!'}`);
            stopTimer();
            await fetchStatus();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Check-out failed');
        } finally { setLoading(false); }
    };
    // ── State derivation ──────────────────────────────────────────────────
    const today        = getLocalToday();
    const isCheckedIn  = !!status?.check_in;
    const hasOpen      = status?.has_open_session === true || (isCheckedIn && !status?.check_out);
    const isFullyOut   = isCheckedIn && !hasOpen && !!status?.check_out;
    const sessionCount = status?.session_count || 0;
    const isMulti      = sessionCount > 1;
    const prevHours    = parseFloat(status?.work_hours || 0);
    const bg = isFullyOut
        ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)'
        : hasOpen
        ? 'linear-gradient(135deg, #fffbeb, #fef3c7)'
        : 'linear-gradient(135deg, #eef2ff, #e0e7ff)';
    // GPS status indicator
    const gpsIcon = gpsStatus === 'ok'      ? <MapPin size={13} className="text-success"/>
                  : gpsStatus === 'fetching' ? <MapPin size={13} className="text-muted"/>
                  : <MapPin size={13} className="text-danger"/>;
    const gpsTooltip = gpsStatus === 'ok'
        ? (gpsData?.location_name || 'Location detected')
        : gpsStatus === 'fetching' ? 'Detecting location…'
        : 'Location not available (permission denied or GPS unavailable)';
    if (status === null) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center gap-3 p-4">
                    <Spinner size="sm" animation="border" variant="primary"/>
                    <span className="text-muted small">Loading attendance…</span>
                </Card.Body>
            </Card>
        );
    }
    // ── Compact view ──────────────────────────────────────────────────────
    if (compact) {
        return (
            <div className="d-flex align-items-center gap-3 p-3 rounded-3" style={{ background: bg }}>
                <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-bold small">{fmtDate(today)}</span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            <Clock size={11} className="me-1"/>{currentTime}
                        </span>
                        <OverlayTrigger placement="top"
                            overlay={<Tooltip>{gpsTooltip}</Tooltip>}>
                            <span style={{ cursor:'help' }}>{gpsIcon}</span>
                        </OverlayTrigger>
                    </div>
                    <div className="d-flex gap-3 flex-wrap" style={{ fontSize: 13 }}>
                        {status.check_in && (
                            <span className="text-success">
                                In: <strong>{fmtTime(status.check_in)}</strong>
                            </span>
                        )}
                        {status.check_out && (
                            <span className="text-primary">
                                Out: <strong>{fmtTime(status.check_out)}</strong>
                            </span>
                        )}
                        {hasOpen && elapsed > 0 && (
                            <span className="text-warning fw-bold">⏱ {fmtElapsed(elapsed)}</span>
                        )}
                        {isFullyOut && prevHours > 0 && (
                            <span className="text-muted">{fmtWorkHours(prevHours)} worked</span>
                        )}
                        {!isCheckedIn && <span className="text-muted">Not checked in</span>}
                    </div>
                </div>
                <div>
                    {!isCheckedIn && (
                        <Button size="sm" variant="success" onClick={handleCheckIn} disabled={loading}>
                            {loading ? <Spinner size="sm"/> : <><LogIn size={14} className="me-1"/>Check In</>}
                        </Button>
                    )}
                    {hasOpen && (
                        <Button size="sm" variant="warning" onClick={handleCheckOut} disabled={loading}>
                            {loading ? <Spinner size="sm"/> : <><LogOut size={14} className="me-1"/>Check Out</>}
                        </Button>
                    )}
                    {isFullyOut && (
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-success px-2 py-1" style={{ fontSize: 11 }}>
                                ✓ Done — {fmtWorkHours(prevHours)}
                            </span>
                            <Button size="sm" variant="outline-success" onClick={handleCheckIn} disabled={loading}>
                                {loading ? <Spinner size="sm"/> : <><LogIn size={13} className="me-1"/>Check In Again</>}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    // ── Full view ─────────────────────────────────────────────────────────
    return (
        <Card className="border-0 shadow-sm mb-4" style={{ background: bg }}>
            <Card.Body className="p-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    {/* Left */}
                    <div>
                        <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                            <div className="fw-bold" style={{ fontSize: 16 }}>
                                Today — {fmtDate(today)}
                            </div>
                            {/* Live clock */}
                            <div className="px-3 py-1 rounded-pill"
                                style={{ background:'rgba(0,0,0,0.08)', fontSize:15,
                                    fontWeight:600, fontFamily:'monospace', letterSpacing:1 }}>
                                <Clock size={13} className="me-1"/>{currentTime}
                            </div>
                            {isMulti && <Badge bg="info" style={{ fontSize:11 }}>{sessionCount} sessions</Badge>}
                            {/* GPS indicator */}
                            <OverlayTrigger placement="top"
                                overlay={<Tooltip id="gps-tip">{gpsTooltip}</Tooltip>}>
                                <div className="d-flex align-items-center gap-1"
                                    style={{ cursor:'help', fontSize:12, color:
                                        gpsStatus==='ok' ? '#059669' :
                                        gpsStatus==='fetching' ? '#6b7280' : '#dc2626' }}>
                                    <MapPin size={14}/>
                                    <span>{
                                        gpsStatus === 'ok'
                                            ? gpsData?.location_name || 'Location detected'
                                            : gpsStatus === 'fetching' ? 'Locating…'
                                            : 'No location'
                                    }</span>
                                </div>
                            </OverlayTrigger>
                        </div>
                        <div className="d-flex gap-3 flex-wrap" style={{ fontSize:14 }}>
                            {status.check_in && (
                                <span className="text-success">
                                    <LogIn size={14} className="me-1"/>In: <strong>{fmtTime(status.check_in)}</strong>
                                </span>
                            )}
                            {status.check_out && (
                                <span className="text-primary">
                                    <LogOut size={14} className="me-1"/>Out: <strong>{fmtTime(status.check_out)}</strong>
                                </span>
                            )}
                            {hasOpen && elapsed > 0 && (
                                <span style={{ color:'#d97706', fontWeight:700 }}>
                                    <Clock size={14} className="me-1"/>{fmtElapsed(elapsed)}
                                    {isMulti && prevHours > 0 && (
                                        <span className="text-muted fw-normal ms-1" style={{ fontSize:12 }}>
                                            · {fmtWorkHours(prevHours)} total
                                        </span>
                                    )}
                                </span>
                            )}
                            {isFullyOut && prevHours > 0 && (
                                <span className="text-muted">
                                    <Clock size={14} className="me-1"/><strong>{fmtWorkHours(prevHours)}</strong> worked
                                </span>
                            )}
                            {!isCheckedIn && <span className="text-muted">Not checked in yet</span>}
                        </div>
                    </div>
                    {/* Right: action buttons */}
                    <div className="d-flex gap-2 align-items-center">
                        {!isCheckedIn && (
                            <Button variant="success" onClick={handleCheckIn}
                                disabled={loading} style={{ minWidth:130 }}>
                                {loading ? <Spinner size="sm" className="me-1"/> : <LogIn size={16} className="me-1"/>}
                                Check In
                            </Button>
                        )}
                        {hasOpen && (
                            <Button variant="warning" onClick={handleCheckOut}
                                disabled={loading} style={{ minWidth:130 }}>
                                {loading ? <Spinner size="sm" className="me-1"/> : <LogOut size={16} className="me-1"/>}
                                Check Out
                            </Button>
                        )}
                        {isFullyOut && (
                            <div className="d-flex align-items-center gap-2">
                                <div className="alert alert-success mb-0 py-2 px-3 fw-bold small">
                                    ✓ Checked Out — {fmtWorkHours(prevHours)} worked
                                </div>
                                <Button variant="outline-success" onClick={handleCheckIn} disabled={loading}>
                                    {loading ? <Spinner size="sm" className="me-1"/> : <LogIn size={15} className="me-1"/>}
                                    Check In Again
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};
export default CheckInWidget;
